import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../../router/all_routes";
import { useT } from "../../../../core/i18n";
import Api from "../../../../core/data/redux/utils/apiCall";
import {
  HallOption,
  displayHallBookings,
  fetchMasterOptions,
  getRequestStatusUi,
  mapOverviewRecord,
  resolveOptionLabel,
} from "./communityHall.service";

const CommunityHallBookingOverview: React.FC = () => {
  const { t } = useT();
  const navigate = useNavigate();
  const routes = all_routes;
  const label = (key: string, fallback: string) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  };

  const [beginDate, setBeginDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [records, setRecords] = useState<ReturnType<typeof mapOverviewRecord>[]>([]);
  const [requestTypes, setRequestTypes] = useState<HallOption[]>([]);
  const [staffQuarters, setStaffQuarters] = useState<HallOption[]>([]);

  useEffect(() => {
    fetchMasterOptions()
      .then((options) => {
        setRequestTypes(options.requestTypes);
        setStaffQuarters(options.staffQuarters);
      })
      .catch(() => {});
  }, []);

  const handleDisplay = async () => {
    if (!beginDate || !endDate) {
      toast.error(label("this_field_is_required", "This field is required"));
      return;
    }
    setLoading(true);
    try {
      const items = await displayHallBookings(beginDate, endDate);
      setRecords(items.map(mapOverviewRecord));
    } catch (err: any) {
      setRecords([]);
      toast.error(err?.message || Api.getErrorMessage(err) || label("something_went_wrong", "Something went wrong"));
    } finally {
      setLoading(false);
      setHasSearched(true);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">{label("view_all_bookings", "View All Bookings")}</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item"><Link to={routes.adminDashboard}>Dashboard</Link></li>
                <li className="breadcrumb-item">
                  <Link to={routes.communityHall.list}>{label("booking_of_community_hall_common_room_park", "Booking of Community Hall/Common Room/Park")}</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">{label("view_all_bookings", "View All Bookings")}</li>
              </ol>
            </nav>
          </div>
          <button type="button" className="btn btn-light" onClick={() => navigate(routes.communityHall.list)}>
            <i className="ti ti-arrow-left me-1"></i>
            {label("back", "Back")}
          </button>
        </div>

        <div className="card border-0 shadow-sm mb-3">
          <div className="card-body">
            <div className="row g-3 align-items-end">
              <div className="col-md-4">
                <label className="form-label fw-semibold">{label("begin_period", "Begin Period")}</label>
                <input type="date" className="form-control" value={beginDate} onChange={(e) => setBeginDate(e.target.value)} />
              </div>
              <div className="col-md-4">
                <label className="form-label fw-semibold">{label("end_period", "End Period")}</label>
                <input type="date" className="form-control" min={beginDate || undefined} value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </div>
              <div className="col-md-4">
                <button type="button" className="btn btn-danger w-100" disabled={loading} onClick={handleDisplay}>
                  {loading ? label("loading", "Loading...") : label("display", "Display")}
                </button>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-5"><div className="spinner-border text-danger" role="status" /></div>
        ) : records.length > 0 ? (
          <div className="row g-3">
            {records.map((record, index) => {
              const statusUi = getRequestStatusUi(record.requestStatus);
              return (
                <div className="col-xl-6" key={`${record.personnelNo}-${index}`}>
                  <div className="card border shadow-sm h-100">
                    <div className="card-header bg-white d-flex justify-content-between align-items-center gap-2">
                      <span className="fw-bold">{record.employeeName}</span>
                      <span className={statusUi.badge}>{statusUi.label}</span>
                    </div>
                    <div className="card-body">
                      <InfoRow label={label("personnel_no", "Personnel No.")} value={record.personnelNo} />
                      <InfoRow label={label("request_type", "Request Type")} value={resolveOptionLabel(requestTypes, record.requestTypeCode)} />
                      <InfoRow label={t("start_date")} value={record.startDate} />
                      <InfoRow label={t("end_date")} value={record.endDate} />
                      <InfoRow label={label("reason", "Reason")} value={record.reason} />
                      <InfoRow label={label("staff_quarter", "Staff Quarters")} value={resolveOptionLabel(staffQuarters, record.staffQuarterCode)} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : hasSearched ? (
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center py-5 text-muted">{label("no_data_found", "No data found")}</div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const InfoRow: React.FC<{ label: string; value: any }> = ({ label, value }) => (
  <div className="d-flex justify-content-between gap-3 mb-2">
    <span className="text-muted fw-semibold">{label}</span>
    <span className="text-dark text-end">{value || "-"}</span>
  </div>
);

export default CommunityHallBookingOverview;
